/**
 * PTEL Visor - Infrastructure Marker Component
 * V05 - Marcador individual con símbolos PTEL PNG
 * 
 * SYM-07c: Integra los símbolos PTEL (180 PNGs) en marcadores Leaflet
 * con selección automática de tamaño según nivel de zoom.
 * 
 * Usa las transformaciones UTM30 → WGS84 de leaflet-crs.ts
 */

import React, { memo, useMemo, useCallback } from 'react';
import { Marker, Tooltip, useMap as _useMap } from 'react-leaflet';
import L from 'leaflet';
import type { NormalizedInfrastructure } from '../../types/infrastructure';
import { VIA_LABELS_LARGO } from '../../utils/procedenciaVia';
import { toWGS84 } from '../../lib/leaflet-crs';
import { getMarkerConfig } from '../../constants/markerConfig';
import { getPTELSymbolPath, getSymbolSizeForZoom, type SizeCode } from '../../hooks/usePTELSymbol';
import { SIZE_TO_PIXELS } from '../../lib/symbols/tipologiaSymbolMatcher';
import { InfrastructurePopup, InfrastructureTooltip } from './InfrastructurePopup';
import './infrastructure.css';

// =============================================================================
// TIPOS
// =============================================================================

export interface InfrastructureMarkerProps {
  /** Infraestructura normalizada */
  infrastructure: NormalizedInfrastructure;
  /** Nivel de zoom actual del mapa (para selección de tamaño de símbolo) */
  zoom?: number;
  /** Mostrar popup al hacer clic (default: true) */
  showPopup?: boolean;
  /** Mostrar tooltip al hover (default: true) */
  showTooltip?: boolean;
  /** Callback al hacer clic */
  onClick?: (infrastructure: NormalizedInfrastructure) => void;
  /** Callback al hover (mouseenter) */
  onHover?: (infrastructure: NormalizedInfrastructure | null) => void;
  /** Marcador seleccionado visualmente */
  selected?: boolean;
  /** Usar símbolos PTEL PNG en lugar de emojis (default: true) */
  usePTELSymbols?: boolean;
  /** Opciones adicionales del popup */
  popupOptions?: {
    showUTM?: boolean;
    showScore?: boolean;
    showSource?: boolean;
    showAddress?: boolean;
  };
}

// =============================================================================
// CONSTANTES
// =============================================================================

/** Tamaño del icono del marcador (emoji legacy) */
const MARKER_SIZE = { width: 32, height: 42 };

/** Cache de iconos Leaflet por tipo (emoji legacy) */
const iconCache = new Map<string, L.DivIcon>();

/** Cache de iconos PTEL PNG por símbolo+tamaño */
const ptelIconCache = new Map<string, L.Icon>();

// =============================================================================
// UTILIDADES
// =============================================================================

/**
 * Crea un icono Leaflet DivIcon para el tipo de infraestructura
 * Usa cache para evitar recrear iconos idénticos
 */
function createInfrastructureIcon(
  type: string,
  iconEmoji: string,
  color: string,
  selected: boolean = false
): L.DivIcon {
  const cacheKey = `${type}-${selected}`;
  
  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey)!;
  }
  
  const selectedClass = selected ? 'ptel-marker-selected' : '';
  
  const html = `
    <div class="ptel-marker-container ${selectedClass}" style="
      width: ${MARKER_SIZE.width}px;
      height: ${MARKER_SIZE.height}px;
      position: relative;
    ">
      <svg viewBox="0 0 32 42" width="${MARKER_SIZE.width}" height="${MARKER_SIZE.height}">
        <path 
          d="M16 0C7.163 0 0 7.163 0 16c0 8.837 16 26 16 26s16-17.163 16-26C32 7.163 24.837 0 16 0z" 
          fill="${color}"
          stroke="#262624"
          stroke-width="1"
        />
        <circle cx="16" cy="16" r="10" fill="#262624" fill-opacity="0.2"/>
      </svg>
      <span style="
        position: absolute;
        top: 8px;
        left: 50%;
        transform: translateX(-50%);
        font-size: 16px;
        line-height: 1;
      ">${iconEmoji}</span>
    </div>
  `;
  
  const divIcon = L.divIcon({
    html,
    className: 'ptel-infrastructure-marker',
    iconSize: [MARKER_SIZE.width, MARKER_SIZE.height],
    iconAnchor: [MARKER_SIZE.width / 2, MARKER_SIZE.height],
    popupAnchor: [0, -MARKER_SIZE.height + 4],
    tooltipAnchor: [0, -MARKER_SIZE.height + 8],
  });
  
  // Solo cachear si no está seleccionado (el estado seleccionado cambia)
  if (!selected) {
    iconCache.set(cacheKey, divIcon);
  }
  
  return divIcon;
}

/**
 * Crea un icono Leaflet con símbolo PTEL PNG
 * Usa cache para evitar recrear iconos idénticos
 * 
 * @param symbolPath - Ruta al PNG del símbolo
 * @param size - Código de tamaño (06mm, 08mm, etc.)
 * @param selected - Si el marcador está seleccionado
 */
function createPTELIcon(
  symbolPath: string,
  size: SizeCode,
  selected: boolean = false,
  centroide: boolean = false
): L.Icon {
  const cacheKey = `${symbolPath}-${size}-${selected}-${centroide}`;
  
  if (ptelIconCache.has(cacheKey)) {
    return ptelIconCache.get(cacheKey)!;
  }
  
  const sizePixels = SIZE_TO_PIXELS[size];
  // Mantener proporción cuadrada para los símbolos PNG
  const iconSize = sizePixels;
  
  // MAPCEN · DOP-2=(a): el centroide municipal lleva su propia clase (símbolo
  // atenuado + anillo discontinuo ámbar en infrastructure.css, sin glow) para
  // que seis iconos apilados en el centro del pueblo no parezcan seis ubicaciones.
  const classes = ['ptel-symbol-marker'];
  if (selected) classes.push('ptel-marker-selected');
  if (centroide) classes.push('ptel-marker-centroide');

  const icon = L.icon({
    iconUrl: symbolPath,
    iconSize: [iconSize, iconSize],
    iconAnchor: [iconSize / 2, iconSize],
    popupAnchor: [0, -iconSize + 4],
    tooltipAnchor: [0, -iconSize + 8],
    className: classes.join(' '),
  });
  
  // Solo cachear si no está seleccionado
  if (!selected) {
    ptelIconCache.set(cacheKey, icon);
  }
  
  return icon;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Marcador individual de infraestructura PTEL
 * 
 * Transforma automáticamente las coordenadas UTM30 a WGS84
 * para su visualización en Leaflet.
 * 
 * Características:
 * - Icono según tipo de infraestructura
 * - Popup con información detallada
 * - Tooltip rápido en hover
 * - Callbacks para interactividad
 * - Memoizado para rendimiento
 * 
 * @example
 * ```tsx
 * <InfrastructureMarker
 *   infrastructure={hospital}
 *   onClick={(infra) => console.log('Clicked:', infra.name)}
 *   showPopup={true}
 * />
 * ```
 */
export const InfrastructureMarker = memo<InfrastructureMarkerProps>(function InfrastructureMarker({
  infrastructure,
  zoom = 15,
  showPopup = true,
  showTooltip = true,
  onClick,
  onHover,
  selected = false,
  usePTELSymbols = true,
  popupOptions = {},
}) {
  const { id, name, type, x, y, address, procedencia } = infrastructure;
  const esCentroide = procedencia === 'centroide';
  
  // Transformar coordenadas UTM30 → WGS84
  const position = useMemo<[number, number]>(() => {
    const [lat, lng] = toWGS84(x, y);
    return [lat, lng];
  }, [x, y]);
  
  // Obtener configuración del marcador (para fallback y accesibilidad)
  const markerConfig = useMemo(() => getMarkerConfig(type), [type]);
  
  // Crear icono Leaflet (PTEL PNG o emoji legacy)
  const leafletIcon = useMemo(() => {
    if (usePTELSymbols) {
      // Usar símbolos PTEL PNG
      const symbolPath = getPTELSymbolPath(name, type, zoom, address);
      const size = getSymbolSizeForZoom(zoom);
      return createPTELIcon(symbolPath, size, selected, esCentroide);
    }
    // Fallback a emoji legacy
    return createInfrastructureIcon(type, markerConfig.icon, markerConfig.color, selected);
  }, [usePTELSymbols, name, type, zoom, address, selected, esCentroide, markerConfig.icon, markerConfig.color]);
  
  // Event handlers
  const handleClick = useCallback(() => {
    onClick?.(infrastructure);
  }, [onClick, infrastructure]);
  
  const handleMouseOver = useCallback(() => {
    onHover?.(infrastructure);
  }, [onHover, infrastructure]);
  
  const handleMouseOut = useCallback(() => {
    onHover?.(null);
  }, [onHover]);
  
  // Event handlers object para Marker
  const eventHandlers = useMemo(() => ({
    click: handleClick,
    mouseover: handleMouseOver,
    mouseout: handleMouseOut,
  }), [handleClick, handleMouseOver, handleMouseOut]);

  return (
    <Marker
      position={position}
      icon={leafletIcon}
      eventHandlers={eventHandlers}
      // Accesibilidad
      alt={`${markerConfig.icon} ${name}`}
      title={name}
      // Key para React
      key={id}
    >
      {/* Popup con información detallada */}
      {showPopup && (
        <InfrastructurePopup
          infrastructure={infrastructure}
          showUTM={popupOptions.showUTM ?? true}
          showScore={popupOptions.showScore ?? true}
          showSource={popupOptions.showSource ?? true}
          showAddress={popupOptions.showAddress ?? true}
        />
      )}
      
      {/* Tooltip rápido; con centroide municipal, el propio marcador lo dice (MAPCEN) */}
      {showTooltip && esCentroide && (
        <Tooltip
          className="ptel-infrastructure-tooltip ptel-tooltip-centroide"
          direction="top"
          offset={[0, -10]}
          opacity={1}
        >
          <span>{markerConfig.icon} {name}</span>
          <br />
          <span className="ptel-tooltip-centroide-via">{VIA_LABELS_LARGO.centroide}</span>
        </Tooltip>
      )}
      {showTooltip && !esCentroide && (
        <InfrastructureTooltip name={name} type={type} />
      )}
    </Marker>
  );
});

// =============================================================================
// EXPORTS
// =============================================================================

export default InfrastructureMarker;

/**
 * Limpia la cache de iconos (legacy emoji + PTEL PNG)
 * Útil para testing o cuando cambia la configuración
 */
export function clearIconCache(): void {
  iconCache.clear();
  ptelIconCache.clear();
}
